import React, { useState } from 'react';
import { Material, CharacterOption } from './Crafting';
import { getRarityIcon } from '../utils/craftingUtils';

interface Props {
  inventory: Material[];
  characters: CharacterOption[];
  onSellMaterial: (materialId: number, quantity: number, characterId: number) => Promise<void>;
  onExchangeMaterial: (materialId: number, sets: number, characterId: number) => Promise<void>;
}

const EXCHANGE_SET_SIZE = 5;

function rarityBadgeClass(rarity: string): string {
  switch (rarity.toLowerCase()) {
    case 'uncommon':  return 'cf-badge--green';
    case 'rare':      return 'cf-badge--blue';
    case 'epic':      return 'cf-badge--purple';
    case 'legendary': return 'cf-badge--amber';
    default:          return 'cf-badge--dim';
  }
}

const CraftingInventoryTab: React.FC<Props> = ({ inventory, characters, onSellMaterial, onExchangeMaterial }) => {
  const [sellCharacterId, setSellCharacterId] = useState<number | null>(characters[0]?.id ?? null);
  const [sellingId, setSellingId] = useState<number | null>(null);
  const [exchangingId, setExchangingId] = useState<number | null>(null);

  const characterId = sellCharacterId ?? characters[0]?.id ?? null;

  const handleSell = async (materialId: number, quantity: number) => {
    if (!characterId) return;
    setSellingId(materialId);
    try {
      await onSellMaterial(materialId, quantity, characterId);
    } finally {
      setSellingId(null);
    }
  };

  const handleExchange = async (materialId: number, quantity: number) => {
    if (!characterId) return;
    const sets = Math.floor(quantity / EXCHANGE_SET_SIZE);
    if (sets < 1) return;
    setExchangingId(materialId);
    try {
      await onExchangeMaterial(materialId, sets, characterId);
    } finally {
      setExchangingId(null);
    }
  };

  return (
    <div className="crafting-tab-content">
      <div className="crafting-section-header crafting-section-header--gold">
        Material Inventory
      </div>

      {characters.length > 0 && inventory.length > 0 && (
        <div className="cf-sell-character-select">
          <label htmlFor="cf-sell-character">Acting as:</label>
          <select
            id="cf-sell-character"
            value={characterId ?? ''}
            onChange={(e) => setSellCharacterId(Number(e.target.value))}
          >
            {characters.map(c => (
              <option key={c.id} value={c.id}>{c.name} ({c.gold ?? 0} gold)</option>
            ))}
          </select>
        </div>
      )}

      {inventory.length === 0 ? (
        <div className="crafting-empty">
          <p>No materials in inventory</p>
          <small>Defeat enemies in battle to collect crafting materials</small>
        </div>
      ) : (
        <div className="crafting-inv-list">
          {inventory.map(item => (
            <div key={item.id} className="crafting-inv-row">
              <div className="crafting-inv-icon">{getRarityIcon(item.materialRarity)}</div>

              <div className="crafting-inv-info">
                <div className="crafting-inv-name">{item.materialName}</div>
                <div className="crafting-inv-meta">
                  <span className="crafting-inv-type">{item.materialType}</span>
                  <span className={`cf-badge ${rarityBadgeClass(item.materialRarity)}`}>
                    {item.materialRarity}
                  </span>
                </div>
                <div className="crafting-inv-desc">{item.description}</div>
              </div>

              <div className="crafting-inv-bar-col">
                <div className="crafting-inv-qty">×{item.quantity}</div>
                <div className="cf-stock-bar">
                  <div
                    className="cf-stock-fill"
                    style={{ width: `${Math.min(100, (item.quantity / Math.max(item.quantity, 20)) * 100)}%`, background: 'var(--gold)' }}
                  />
                </div>
                <div className="crafting-inv-value">{item.baseValue}g each</div>
              </div>

              {characters.length > 0 && (
                <div className="cf-disposal-actions">
                  <button
                    className="cf-action-btn cf-action-btn--sell"
                    onClick={() => handleSell(item.materialId, item.quantity)}
                    disabled={sellingId === item.materialId || !characterId}
                    title={`Sell all ${item.quantity} for ${item.sellValue * item.quantity} gold`}
                  >
                    {sellingId === item.materialId ? 'Selling…' : `Sell (+${item.sellValue * item.quantity}g)`}
                  </button>
                  {item.materialRarity.toLowerCase() !== 'legendary' && item.quantity >= EXCHANGE_SET_SIZE && (
                    <button
                      className="cf-action-btn cf-action-btn--exchange"
                      onClick={() => handleExchange(item.materialId, item.quantity)}
                      disabled={exchangingId === item.materialId || !characterId}
                      title={`Refine ${Math.floor(item.quantity / EXCHANGE_SET_SIZE) * EXCHANGE_SET_SIZE}x ${item.materialName} into ${Math.floor(item.quantity / EXCHANGE_SET_SIZE)}x of the next tier (gold fee)`}
                    >
                      {exchangingId === item.materialId
                        ? 'Refining…'
                        : `Refine ${Math.floor(item.quantity / EXCHANGE_SET_SIZE)}x ↑`}
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CraftingInventoryTab;
